/**
 * BenchmarkWidget Component (Story 5.29, Task 3 → Epic 37 redesign)
 *
 * Renders industry benchmark comparisons with per-metric cards.
 * Three states: edit mode (placeholder), preview mode (data), free tier (blur + CTA).
 *
 * Outer chrome (title + padding + loading/error/empty/centered states) comes from
 * the shared chart-widget shell, so the canvas widget and the sidebar preview read
 * as one family. Metric cards use the clean look (subtle border, rounded, tabular
 * nums, muted labels).
 *
 * Colorblind-safe badge system: blue-600 / gray-500 / amber-500
 * (NOT green/gray/orange — fails deuteranopia). KEPT — semantic + a11y.
 */

import { useState } from "react";
import { Crosshair, ArrowUp, ArrowDown, Minus, Warning } from "@phosphor-icons/react";
import type { WidgetConfig } from "../../../lib/templateUtils";
import type { WidgetDataEnvelope, AnyWidgetPayload, BenchmarkData, BenchmarkItem } from "../../../lib/widgetData";
import { isBenchmarkData } from "../../../lib/widgetData";
import { formatBenchmarkMetricValue } from "@provenleap/types/report-rendering";
import { usePlanLimits } from "../../../hooks/usePlanLimits";
import { useNavigate } from "@tanstack/react-router";
import { Button } from "@provenleap/ui";
import { useChartWidgetShell, ChartMessage } from "./chartWidgetShell";

/** Metric display labels — kept in sync with packages/db/src/seed/benchmark-data.ts */
const BENCHMARK_METRIC_META: Record<string, { shortLabel: string }> = {
  ctr: { shortLabel: "CTR" },
  cpc: { shortLabel: "CPC" },
  cpa: { shortLabel: "CPA" },
  conversion_rate: { shortLabel: "Conv. Rate" },
  bounce_rate: { shortLabel: "Bounce Rate" },
  avg_session_duration: { shortLabel: "Avg. Session" },
};

// ── Badge System (Colorblind-Safe) ──────────────────────────────────

type BadgeLevel = "above" | "average" | "below";

const BADGE_CONFIG: Record<
  BadgeLevel,
  { label: string; color: string; tailwind: string; Icon: typeof ArrowUp }
> = {
  above: { label: "Above Average", color: "#2563eb", tailwind: "text-blue-600", Icon: ArrowUp },
  average: { label: "Average", color: "#6b7280", tailwind: "text-gray-500", Icon: Minus },
  below: { label: "Below Average", color: "#f59e0b", tailwind: "text-amber-500", Icon: ArrowDown },
};

// ── Metric Value Formatting (shared via @provenleap/types) ──────────

const formatMetricValue = formatBenchmarkMetricValue;

// ── Props ────────────────────────────────────────────────────────────

interface BenchmarkWidgetProps {
  widget: WidgetConfig;
  data?: WidgetDataEnvelope<AnyWidgetPayload>;
  isPreviewMode?: boolean;
}

// ── Component ────────────────────────────────────────────────────────

export function BenchmarkWidget({ widget, data, isPreviewMode }: BenchmarkWidgetProps) {
  const { plan } = usePlanLimits();
  const navigate = useNavigate();
  const config = widget.config as Record<string, unknown>;
  const industry = (config.industry as string) ?? "";
  const metrics = (config.metrics as string[]) ?? [];
  const showPercentileRange = (config.showPercentileRange as boolean) ?? true;
  const showBadge = (config.showBadge as boolean) ?? true;
  const metricVisibility = (config.metricVisibility as Record<string, boolean>) ?? {};
  const isFreeTier = plan === "free";
  const { shell } = useChartWidgetShell(widget.config);

  // Free tier: blurred placeholder with upgrade CTA
  if (isFreeTier) {
    return shell(
      "benchmark",
      widget.title,
      <div className="relative min-h-0 flex-1">
        <div aria-hidden="true" className="pointer-events-none h-full select-none blur-[12px]">
          <BenchmarkPlaceholder industry={industry} metricCount={metrics.length} />
        </div>
        <div className="absolute inset-0 flex items-center justify-center bg-background/60">
          <div className="space-y-3 px-4 text-center">
            <p className="text-sm font-semibold text-foreground">
              See how you compare to your industry
            </p>
            <p className="text-xs text-muted-foreground">
              Upgrade to Team to unlock benchmark comparisons
            </p>
            <Button
              size="sm"
              onClick={() =>
                navigate({
                  to: "/settings/billing",
                  search: { upgrade: "true", plan: "starter" },
                })
              }
            >
              Upgrade to Team
            </Button>
          </div>
        </div>
      </div>,
    );
  }

  // Edit mode: static placeholder
  if (!isPreviewMode) {
    return shell(
      "benchmark",
      widget.title,
      <div className="min-h-0 flex-1">
        <BenchmarkPlaceholder industry={industry} metricCount={metrics.length} />
      </div>,
    );
  }

  // Preview/Generate mode: render data
  const benchmarkData = data?.data;

  if (data?.loading) {
    return shell(
      "benchmark",
      widget.title,
      <div className="w-full animate-pulse space-y-3">
        <div className="h-4 w-3/4 rounded bg-muted" />
        <div className="h-20 rounded bg-muted" />
        <div className="h-20 rounded bg-muted" />
      </div>,
    );
  }

  if (data?.error) {
    return shell(
      "benchmark",
      widget.title,
      <ChartMessage role="alert">{data.error}</ChartMessage>,
      { centered: true },
    );
  }

  if (!benchmarkData || !isBenchmarkData(benchmarkData)) {
    // No integrations connected
    return shell(
      "benchmark",
      widget.title,
      <div className="min-h-0 flex-1">
        <EmptyState />
      </div>,
    );
  }

  return shell(
    "benchmark",
    widget.title,
    <div className="min-h-0 flex-1 overflow-auto">
      <BenchmarkDataView
        benchmarkData={benchmarkData}
        showPercentileRange={showPercentileRange}
        showBadge={showBadge}
        metricVisibility={metricVisibility}
      />
    </div>,
  );
}

// ── Edit Mode Placeholder ────────────────────────────────────────────

function BenchmarkPlaceholder({ industry, metricCount }: { industry: string; metricCount: number }) {
  return (
    <div
      data-testid="benchmark-placeholder"
      className="flex h-full flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-border bg-muted/20 p-4"
    >
      <Crosshair size={28} weight="duotone" className="text-primary" />
      <span className="text-sm font-semibold text-foreground">Benchmark Comparison</span>
      {industry && (
        <span className="text-xs text-muted-foreground">
          {industry.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}
        </span>
      )}
      {metricCount > 0 && (
        <span
          data-testid="benchmark-metric-count"
          className="inline-flex items-center rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary"
        >
          {metricCount} metric{metricCount !== 1 ? "s" : ""}
        </span>
      )}
    </div>
  );
}

// ── Data View (Preview/Generate) ────────────────────────────────────

function BenchmarkDataView({
  benchmarkData,
  showPercentileRange,
  showBadge,
  metricVisibility,
}: {
  benchmarkData: BenchmarkData;
  showPercentileRange: boolean;
  showBadge: boolean;
  metricVisibility: Record<string, boolean>;
}) {
  const [expanded, setExpanded] = useState(false);
  const DEFAULT_VISIBLE = 4;

  const { summary, items, dataQuality } = benchmarkData;

  // Filter by visibility config
  const visibleItems = items.filter(
    (item) => metricVisibility[item.metricName] !== false,
  );

  const displayItems = expanded ? visibleItems : visibleItems.slice(0, DEFAULT_VISIBLE);
  const hiddenCount = visibleItems.length - DEFAULT_VISIBLE;

  return (
    <div className="space-y-3">
      {/* Summary headline */}
      <div data-testid="benchmark-summary" className="text-center">
        <p className="text-sm font-semibold text-foreground">
          Outperforming industry average on {summary.above} of {summary.total} metrics
        </p>
      </div>

      {/* Metric cards */}
      <div className="space-y-2">
        {displayItems.map((item) => (
          <MetricCard
            key={`${item.metricName}-${item.platform}`}
            item={item}
            showPercentileRange={showPercentileRange}
            showBadge={showBadge}
          />
        ))}
      </div>

      {/* Show more button */}
      {!expanded && hiddenCount > 0 && (
        <button
          data-testid="benchmark-show-more"
          onClick={() => setExpanded(true)}
          className="w-full py-1 text-xs text-primary hover:underline"
        >
          Show {hiddenCount} more
        </button>
      )}

      {/* Partial data footnote */}
      {dataQuality.missingMetrics.length > 0 && (
        <p data-testid="benchmark-missing-note" className="text-xs text-muted-foreground">
          {dataQuality.missingMetrics.length} metric{dataQuality.missingMetrics.length !== 1 ? "s" : ""} unavailable
          — connect additional integrations to see more
        </p>
      )}

      {/* Data provenance footer */}
      {items.length > 0 && (
        <p data-testid="benchmark-provenance" className="text-center text-xs text-muted-foreground">
          Benchmarks from {items[0]!.source}, {items[0]!.effectiveYear}
        </p>
      )}
    </div>
  );
}

// ── Per-Metric Card ────────────────────────────────────────────────

function MetricCard({
  item,
  showPercentileRange,
  showBadge,
}: {
  item: BenchmarkItem;
  showPercentileRange: boolean;
  showBadge: boolean;
}) {
  const badge = BADGE_CONFIG[item.badge];
  const meta = BENCHMARK_METRIC_META[item.metricName];
  const label = meta?.shortLabel ?? item.metricName;
  const unit = item.unit;

  // Normalize values for the bar (percentage of max scale)
  const maxScale = Math.max(item.p75, item.clientValue) * 1.2;
  const clientPct = maxScale > 0 ? (item.clientValue / maxScale) * 100 : 0;
  const medianPct = maxScale > 0 ? (item.median / maxScale) * 100 : 0;
  const p25Pct = maxScale > 0 ? (item.p25 / maxScale) * 100 : 0;
  const p75Pct = maxScale > 0 ? (item.p75 / maxScale) * 100 : 0;

  // Stale data check (>30 days)
  const isStale = Date.now() - item.lastUpdated > 30 * 24 * 60 * 60 * 1000;

  return (
    <div
      data-testid={`benchmark-card-${item.metricName}`}
      className="space-y-2 rounded-lg border border-border p-3"
      aria-label={`${label}: ${badge.label}, ${formatMetricValue(item.clientValue, unit)} vs industry median ${formatMetricValue(item.median, unit)}`}
    >
      {/* Header row: metric name + platform */}
      <div className="flex items-center justify-between">
        <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">{label}</span>
        <div className="flex items-center gap-1">
          {isStale && (
            <Warning
              size={12}
              className="text-amber-500"
              data-testid="stale-warning"
              aria-label="Data is older than 30 days"
            />
          )}
          <span className="text-[11px] capitalize text-muted-foreground">
            {item.platform.replace(/-/g, " ")}
          </span>
        </div>
      </div>

      {/* Values row */}
      <div className="flex items-baseline gap-3">
        <span className="text-lg font-semibold tabular-nums text-foreground">
          {formatMetricValue(item.clientValue, unit)}
        </span>
        <span className="text-xs tabular-nums text-muted-foreground">
          Industry avg: {formatMetricValue(item.median, unit)}
        </span>
      </div>

      {/* Mini horizontal bar */}
      <div className="relative h-2 overflow-hidden rounded-full bg-muted" aria-hidden="true">
        {/* P25-P75 shaded range */}
        <div
          className="absolute h-full bg-muted-foreground/20"
          style={{ left: `${p25Pct}%`, width: `${Math.max(0, p75Pct - p25Pct)}%` }}
        />
        {/* Client value bar */}
        <div
          className="absolute h-full rounded-full"
          style={{ width: `${clientPct}%`, backgroundColor: badge.color }}
        />
        {/* Median reference mark */}
        <div
          className="absolute h-full w-0.5 bg-foreground/70"
          style={{ left: `${medianPct}%` }}
        />
      </div>

      {/* P25-P75 range text */}
      {showPercentileRange && (
        <p className="text-xs tabular-nums text-muted-foreground">
          Range: {formatMetricValue(item.p25, unit)} — {formatMetricValue(item.p75, unit)}
        </p>
      )}

      {/* Performance badge */}
      {showBadge && (
        <div
          data-testid={`benchmark-badge-${item.metricName}`}
          className={`flex items-center gap-1 ${badge.tailwind}`}
        >
          <badge.Icon size={14} weight="bold" />
          <span className="text-xs font-medium">{badge.label}</span>
        </div>
      )}
    </div>
  );
}

// ── Empty State ──────────────────────────────────────────────────────

function EmptyState() {
  const navigate = useNavigate();
  return (
    <div
      data-testid="benchmark-empty"
      className="flex h-full flex-col items-center justify-center gap-3 p-4 text-center"
    >
      <Crosshair size={32} className="text-muted-foreground" />
      <p className="text-sm text-muted-foreground">
        Connect an integration to compare your performance
      </p>
      <Button
        variant="outline"
        size="sm"
        onClick={() => navigate({ to: "/dashboard" as string })}
      >
        Go to Integrations
      </Button>
    </div>
  );
}
